import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { ToggleButton, ToggleButtonGroup } from '@sunwebgroup/uds-ui/toggle-button'
import cn from 'classnames'
import get from 'lodash/get'
import * as React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useIntl } from '@/intl'
import { getErrorMessage } from '@/scopes/common/components/FormElements/getErrorMessage'
import { useStatus } from '@/scopes/common/components/FormElements/useStatus'
import styles from './ToggleButtonGroupInput.module.css'

interface ToggleButtonGroupInputProps {
  items: { value: string, title: string }[]
  label?: string
  name: string
  patternErrorMessage?: string
  required?: boolean
  requiredErrorMessage?: string
  formFieldClass?: string
  disabled?: boolean
  helpText?: string
}

export function ToggleButtonGroupInput({
  items,
  label,
  name,
  patternErrorMessage,
  required,
  requiredErrorMessage,
  formFieldClass,
  disabled,
  helpText,
}: ToggleButtonGroupInputProps) {
  const {
    control,
    formState,
  } = useFormContext()

  const { errors, defaultValues } = formState
  const intl = useIntl()
  const defaultValue = get(defaultValues, name)
  const errorMessage = getErrorMessage(
    name,
    errors,
    { patternErrorMessage, requiredErrorMessage },
  )

  const {
    field: {
      onChange,
      onBlur,
      disabled: fieldDisabled,
      ref,
    },
  } = useController({
    name,
    control,
    disabled,
    rules: {
      required,
    },
  })

  const status = useStatus({
    name,
    required,
    shouldMarkValidStatus: value => Boolean(value),
  })

  return (
    <FormField
      feedbackText={errorMessage}
      status={status}
      label={label}
      optionalLabel={!required ? intl.formatMessage({ id: 'common_form_field_optional_label' }) : undefined}
      className={formFieldClass}
      helpText={helpText}
      disabled={fieldDisabled}
    >
      <input
        id={name}
        ref={ref}
        type="text"
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className={styles.hiddenInput}
      />
      <ToggleButtonGroup
        type="single"
        defaultValue={defaultValue}
        onValueChange={onChange}
        onBlur={onBlur}
        disabled={fieldDisabled}
        className={cn(styles.buttonGroup)}
      >
        {items.map(({ title, value }) => (
          <ToggleButton
            value={value}
            key={`${name}${title}`}
            title={title}
            disabled={fieldDisabled}
          />
        ))}
      </ToggleButtonGroup>
    </FormField>
  )
}
